import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  CheckCircle, Phone, Calculator, FileText, Award, Clock, Home, Building2, 
  MapPin, Leaf, Zap, Sun, Wind, ThermometerSun, ArrowRight, Shield,
  Globe, Users, Wrench, Lightbulb
} from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import RelatedCaseStudies from '@/components/RelatedCaseStudies';
import MethodologyCallout from '@/components/MethodologyCallout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { pages as pageContent } from '@/data/pageContent';

const SAPCalculations = () => {
  // pageContent is the single source of truth for FAQs (and their FAQPage
  // schema) on this route — the SSG build script consumes the same module,
  // so build-time HTML and React runtime render an identical FAQ list.
  const routeContent = pageContent['/sap-calculations'];
  const faqs = routeContent.faqs.map((f) => ({ question: f.q, answer: f.a }));

  const specialistConstructionTypes = [
    { name: "Cob Construction", description: "Traditional earth building requiring careful thermal mass modelling" },
    { name: "Hempcrete & Hemp-Lime", description: "Bio-based insulation with unique thermal and hygroscopic properties" },
    { name: "Straw Bale Construction", description: "High-performance natural insulation requiring specialist U-value calculations" },
    { name: "SIPs (Structural Insulated Panels)", description: "Factory-built panels with excellent thermal performance" },
    { name: "ICF (Insulated Concrete Formwork)", description: "Combined structure and insulation requiring thermal bridging analysis" },
    { name: "Timber Frame & Hybrid Builds", description: "Mixed construction methods requiring detailed element modelling" }
  ];

  const lowCarbonSystems = [
    { icon: ThermometerSun, name: "Heat Pumps", description: "Air source (ASHP) and ground source (GSHP) systems" },
    { icon: Sun, name: "Solar PV & Battery", description: "Photovoltaic systems with storage integration" },
    { icon: Wind, name: "MVHR Systems", description: "Mechanical ventilation with heat recovery" },
    { icon: Leaf, name: "Fabric-First Design", description: "Passive and low-energy building strategies" }
  ];

  // FAQ Schema for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  // Service Schema
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "SAP Calculations",
    "name": "SAP Calculations & New Build EPCs - UK Wide",
    "description": "Professional SAP calculations for Building Regulations Part L compliance. Design stage and as-built SAP assessments for new dwellings, conversions, and extensions. Specialist expertise in non-standard, low-carbon, and sustainable construction.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Energy Performance Direct",
      "telephone": "+44-1392-980077",
      "email": "info@energyperformancedirect.co.uk",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "GB"
      }
    },
    "areaServed": [
      { "@type": "Country", "name": "England" },
      { "@type": "Country", "name": "Wales" },
      { "@type": "Country", "name": "Scotland" }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "SAP Calculation Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Design Stage SAP Calculations",
            "description": "Pre-construction SAP calculations for Part L compliance and Building Control submission"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "As-Built SAP Calculations",
            "description": "Completion stage SAP calculations and EPC lodgement"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "New Build EPCs",
            "description": "Energy Performance Certificates for new dwellings"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "SAP for Non-Standard Construction",
            "description": "Specialist SAP modelling for hempcrete, straw bale, cob, SIPs, ICF and timber frame buildings"
          }
        }
      ]
    }
  };

  return (
    <>
      <Helmet>
        <title>SAP Calculations UK | New Build EPCs | Future-Ready HEM Compliance | Energy Performance Direct</title>
        <meta name="description" content="UK-wide SAP calculations for self-builders & developers. Design stage SAP, as-built assessments, new build EPCs. Specialists in hempcrete, straw bale, SIPs, passive house. Future Homes Standard ready. Call 01392 980077." />
        <meta name="keywords" content="SAP calculations UK, SAP assessor UK, SAP calculations for self build, SAP calculations for developers, new build EPC UK, SAP for straw bale, SAP for hempcrete, SAP for cob construction, SAP for SIPs, SAP for ICF, SAP for passive house, Home Energy Model, HEM energy modelling, Future Homes Standard" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/sap-calculations" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <Navbar />

        {/* Hero Section */}
        <section className="py-16 lg:py-20" style={{ background: 'linear-gradient(135deg, #ecfdf5 0%, #cffafe 50%, #ecfdf5 100%)' }}>
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto text-center">
              <div className="inline-block mb-4 px-4 py-2 bg-white rounded-full shadow-sm">
                <span className="text-sm font-medium text-green-600">Building Regulations Part L Compliance | UK-Wide Service</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                SAP Calculations & New Build EPCs{' '}
                <span className="block text-green-600 text-3xl md:text-4xl mt-2">UK-Wide | Future-Ready HEM Compliance</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
                Professional remote SAP calculations, as-built assessments, and new build EPCs for projects across the UK. 
                Specialist expertise in non-standard, low-carbon, and sustainable construction methods.
              </p>
              <div className="flex flex-wrap justify-center gap-3 mb-8">
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <Globe className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium">UK-Wide Coverage</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <Leaf className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium">Sustainable Build Specialists</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <Zap className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium">Fast Turnaround</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <Shield className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium">HEM Future-Ready</span>
                </div>
              </div>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/booking?service=sap_calculation">
                  <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white">
                    Get SAP Quote
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/quick-quote">
                  <Button size="lg" variant="outline" className="border-cyan-600 text-cyan-700 hover:bg-cyan-50">
                    Request a Custom Quote
                  </Button>
                </Link>
                <a href="tel:01392980077">
                  <Button size="lg" variant="outline" className="border-green-600 text-green-600 hover:bg-green-50">
                    <Phone className="mr-2 h-5 w-5" />
                    01392 980077
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Core Service Description */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-8">UK-Wide SAP Calculations & Energy Assessments</h2>
              <div className="prose prose-lg max-w-none text-gray-700">
                <p className="text-lg leading-relaxed mb-6">
                  Energy Performance Direct provides <strong>remote SAP calculations</strong>, as-built assessments, and <strong>new build EPCs</strong> for 
                  projects across the UK. Our service supports design-stage optimisation, Building Regulations compliance, and final 
                  certification, with specialist expertise in non-standard, low-carbon, and sustainable construction.
                </p>
                <div className="grid md:grid-cols-2 gap-6 my-8">
                  <div className="bg-green-50 p-6 rounded-lg">
                    <h3 className="font-semibold text-lg mb-3 text-gray-900">We Work With:</h3>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                        <span>Self-builders & custom-build projects</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                        <span>Small and large developers</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                        <span>Passive and low-energy designers</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                        <span>Off-grid and near-off-grid projects</span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-cyan-50 p-6 rounded-lg">
                    <h3 className="font-semibold text-lg mb-3 text-gray-900">Remote Delivery Benefits:</h3>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-cyan-600 flex-shrink-0" />
                        <span>Faster turnaround times</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-cyan-600 flex-shrink-0" />
                        <span>Competitive pricing</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-cyan-600 flex-shrink-0" />
                        <span>Nationwide coverage</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-cyan-600 flex-shrink-0" />
                        <span>Access to specialist expertise</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Design Stage SAP */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">SAP Calculations at Design Stage (UK-Wide)</h2>
                  <p className="text-lg text-gray-700 mb-6">
                    Design-stage SAP calculations are essential for demonstrating Part L compliance before construction begins. 
                    They enable you to optimise your design for energy performance and Building Regulations approval.
                  </p>
                  <h3 className="font-semibold text-xl mb-4">Design Stage SAP is used to:</h3>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Demonstrate Part L compliance for Building Control</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Optimise fabric and system performance</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Compare heating, ventilation, and renewable strategies</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Reduce construction risk before work starts on site</span>
                    </li>
                  </ul>
                  <Link to="/booking?service=sap_calculation">
                    <Button className="bg-green-600 hover:bg-green-700">
                      Request Design Stage SAP
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
                <div className="bg-white p-8 rounded-2xl shadow-lg">
                  <h3 className="font-semibold text-xl mb-6 text-center">Particularly Suited To:</h3>
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 p-4 bg-green-50 rounded-lg">
                      <Leaf className="h-8 w-8 text-green-600" />
                      <div>
                        <p className="font-medium">Natural Building Methods</p>
                        <p className="text-sm text-gray-600">Cob, hempcrete, straw bale</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-4 bg-cyan-50 rounded-lg">
                      <Building2 className="h-8 w-8 text-cyan-600" />
                      <div>
                        <p className="font-medium">Modern Methods of Construction</p>
                        <p className="text-sm text-gray-600">SIPs, ICF, modular builds</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-lg">
                      <ThermometerSun className="h-8 w-8 text-amber-600" />
                      <div>
                        <p className="font-medium">Passive & Low-Energy Homes</p>
                        <p className="text-sm text-gray-600">Fabric-first, Passivhaus-style</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-4 bg-purple-50 rounded-lg">
                      <Users className="h-8 w-8 text-purple-600" />
                      <div>
                        <p className="font-medium">Self-Build & Custom-Build</p>
                        <p className="text-sm text-gray-600">Individual plots, unique designs</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* As-Built SAP & New Build EPCs */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">SAP As-Built Calculations & New Build EPCs</h2>
              <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
                Completion stage assessments using final construction details for Building Control sign-off and EPC lodgement.
              </p>
              <div className="grid md:grid-cols-2 gap-8">
                <Card className="border-2 border-green-100 hover:border-green-300 transition-colors">
                  <CardHeader>
                    <FileText className="h-12 w-12 text-green-600 mb-2" />
                    <CardTitle className="text-2xl">SAP As-Built Calculations</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">
                      As-built SAP calculations use your final construction details to confirm Part L compliance 
                      and generate your Energy Performance Certificate.
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span>Final specification verification</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span>Consistency check with design stage</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span>Building Control completion support</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
                <Card className="border-2 border-cyan-100 hover:border-cyan-300 transition-colors">
                  <CardHeader>
                    <Award className="h-12 w-12 text-cyan-600 mb-2" />
                    <CardTitle className="text-2xl">New Build EPCs</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">
                      New build EPCs are generated from your as-built SAP calculation and lodged on the 
                      national register for Building Control sign-off.
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-cyan-600" />
                        <span>National register lodgement</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-cyan-600" />
                        <span>Building Regulations completion</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-cyan-600" />
                        <span>Valid for 10 years</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
              <div className="mt-8 p-6 bg-gray-50 rounded-xl">
                <h3 className="font-semibold text-lg mb-4">Our Process Ensures:</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                    <span>Consistency between design intent and as-built performance</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                    <span>Accurate representation of low-carbon technologies</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                    <span>Compliance with current SAP conventions and BRE guidance</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Specialist Construction Types */}
        <section className="py-16" style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #ecfdf5 100%)' }}>
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">SAP for Non-Standard & Sustainable Construction</h2>
              <p className="text-lg text-gray-700 text-center mb-12 max-w-3xl mx-auto">
                Energy Performance Direct has specialist experience modelling SAP for natural and modern methods of construction 
                that require careful interpretation within the SAP methodology.
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {specialistConstructionTypes.map((type, index) => (
                  <Card key={index} className="bg-white hover:shadow-lg transition-shadow">
                    <CardContent className="pt-6">
                      <Wrench className="h-10 w-10 text-amber-600 mb-4" />
                      <h3 className="text-xl font-semibold mb-2">{type.name}</h3>
                      <p className="text-gray-600">{type.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <p className="text-center mt-8 text-gray-700 italic">
                These construction methods require careful interpretation within SAP to ensure compliance 
                while reflecting real-world performance.
              </p>
            </div>
          </div>
        </section>

        {/* Low-Carbon Systems */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">SAP Modelling for Low-Energy & Off-Grid Homes</h2>
              <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
                We regularly model low-carbon heating, renewable energy systems, and fabric-first design strategies. 
                Our approach focuses on performance-led compliance, not box-ticking.
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {lowCarbonSystems.map((system, index) => (
                  <Card key={index} className="text-center hover:shadow-lg transition-shadow border-2 border-transparent hover:border-green-200">
                    <CardContent className="pt-6">
                      <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                        <system.icon className="h-8 w-8 text-green-600" />
                      </div>
                      <h3 className="text-lg font-semibold mb-2">{system.name}</h3>
                      <p className="text-sm text-gray-600">{system.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Future Homes Standard / HEM */}
        <section className="py-16" style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%)' }}>
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <div className="inline-block mb-4 px-4 py-2 bg-white rounded-full shadow-sm">
                  <span className="text-sm font-medium text-blue-600">Future-Ready Energy Assessment</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Future-Ready: SAP Today, HEM Tomorrow</h2>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 mb-6">
                  SAP remains the current compliance methodology for new build dwellings. However, the UK government 
                  is transitioning toward the <strong>Home Energy Model (HEM)</strong> as part of the <strong>Future Homes Standard</strong>.
                </p>
                <h3 className="font-semibold text-xl mb-4">Energy Performance Direct:</h3>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <Lightbulb className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Designs SAP models in a <strong>HEM-ready way</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Lightbulb className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Focuses on <strong>fabric performance, system efficiency, and real-world energy use</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Lightbulb className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Supports clients <strong>preparing for future regulatory changes</strong></span>
                  </li>
                </ul>
                <p className="text-gray-600 italic border-l-4 border-blue-400 pl-4">
                  This ensures projects remain robust, adaptable, and compliant as regulations evolve.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* National Coverage */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">UK-Wide SAP Calculations – Fully Remote Service</h2>
              <p className="text-lg text-gray-600 text-center mb-12">
                We provide SAP calculations and EPC services nationwide with fast turnaround and competitive pricing.
              </p>
              <div className="grid md:grid-cols-3 gap-8">
                <Card className="text-center">
                  <CardContent className="pt-6">
                    <MapPin className="h-12 w-12 text-red-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">England</h3>
                    <p className="text-gray-600">Full coverage for all English regions including London, South East, South West, Midlands, North</p>
                  </CardContent>
                </Card>
                <Card className="text-center">
                  <CardContent className="pt-6">
                    <MapPin className="h-12 w-12 text-green-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Wales</h3>
                    <p className="text-gray-600">Complete SAP services for Welsh projects under Part L Wales Building Regulations</p>
                  </CardContent>
                </Card>
                <Card className="text-center">
                  <CardContent className="pt-6">
                    <MapPin className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Scotland</h3>
                    <p className="text-gray-600">SAP-based assessments where applicable for Scottish Building Standards</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Trust & Compliance */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-8">Accurate, Transparent, Regulation-Aligned</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start gap-4 p-4">
                  <Shield className="h-8 w-8 text-green-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold mb-1">BRE SAP Conventions</h3>
                    <p className="text-gray-600">All calculations follow current BRE SAP conventions and guidance</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4">
                  <FileText className="h-8 w-8 text-green-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold mb-1">Part L Aligned</h3>
                    <p className="text-gray-600">Full alignment with Building Regulations Part L requirements</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4">
                  <Building2 className="h-8 w-8 text-green-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold mb-1">Building Control Ready</h3>
                    <p className="text-gray-600">Documentation suitable for submission to Building Control</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4">
                  <Award className="h-8 w-8 text-green-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold mb-1">Accredited Lodgement</h3>
                    <p className="text-gray-600">EPC lodgement via approved accreditation schemes</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-8">SAP Calculation FAQs</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <Card key={index}>
                    <CardContent className="pt-6">
                      <h3 className="font-semibold text-lg mb-2">{faq.question}</h3>
                      <p className="text-gray-600">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Internal Links Section */}
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-2xl font-bold mb-6">Related Services & Resources</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Link to="/u-value-calculations" className="p-4 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors group">
                  <h3 className="font-semibold group-hover:text-green-600">U-Value Calculations</h3>
                  <p className="text-sm text-gray-600">Thermal transmittance for building elements</p>
                </Link>
                <Link to="/psi-value-calculations" className="p-4 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors group">
                  <h3 className="font-semibold group-hover:text-green-600">Thermal Bridge (Psi-Value)</h3>
                  <p className="text-sm text-gray-600">Junction heat loss for Part L compliance</p>
                </Link>
                <Link to="/part-o-overheating" className="p-4 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors group">
                  <h3 className="font-semibold group-hover:text-green-600">Part O Overheating</h3>
                  <p className="text-sm text-gray-600">Overheating risk assessments for new builds</p>
                </Link>
                <Link to="/resources/sap-vs-epc" className="p-4 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors group">
                  <h3 className="font-semibold group-hover:text-green-600">SAP vs EPC Guide</h3>
                  <p className="text-sm text-gray-600">Understanding the difference</p>
                </Link>
              </div>
              <div className="mt-6 grid md:grid-cols-3 gap-4">
                <Link to="/services/domestic_epc" className="p-4 bg-cyan-50 rounded-lg hover:bg-cyan-100 transition-colors group">
                  <h3 className="font-semibold group-hover:text-cyan-700">Domestic EPCs</h3>
                  <p className="text-sm text-gray-600">Energy Performance Certificates for existing properties</p>
                </Link>
                <Link to="/services/commercial_epc" className="p-4 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors group">
                  <h3 className="font-semibold group-hover:text-amber-700">Commercial EPCs</h3>
                  <p className="text-sm text-gray-600">Non-domestic energy assessments</p>
                </Link>
                <Link to="/contact" className="p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors group">
                  <h3 className="font-semibold group-hover:text-green-700">Energy Consultancy</h3>
                  <p className="text-sm text-gray-600">Expert guidance for complex projects</p>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Related case studies — SAP focus, featuring Sticklepath conversion */}
        <RelatedCaseStudies
          title="See SAP in practice"
          subtitle="The Sticklepath conversion case study shows exactly what an as-built SAP looks like — fabric performance, U-values, system penalties and the path to a higher rating with early engagement."
          routes={['/case-study-sticklepath-sap-conversion', '/case-study-exeter-office-g-to-a', '/case-study-london-mees-unit-split']}
          limit={3}
        />

        <MethodologyCallout
          context="Where SAP work sits inside a broader domestic advice engagement,"
          reportCode="DEAR (Domestic Energy Advice Report)"
        />

        {/* CTA Section */}
        <section className="py-16" style={{ background: 'linear-gradient(135deg, #059669 0%, #0d9488 100%)' }}>
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Need SAP Calculations?</h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Professional SAP assessments for self-builders, developers, and sustainable construction projects across the UK
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/booking?service=sap_calculation">
                <Button size="lg" className="bg-white text-green-600 hover:bg-gray-100">
                  Get SAP Quote
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="tel:01392980077">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  <Phone className="mr-2 h-5 w-5" />
                  01392 980077
                </Button>
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default SAPCalculations;
